import React, {useEffect, useState} from 'react';
import {Box, Button, FormGroup, IconButton, MenuItem, Select, Snackbar, Stack, TextField, Tooltip} from '@mui/material';
import Group from '../../../../@types/group';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import {uuid} from '../../../../utils/utils';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import {ResourceType} from '../../../../@types/resource-type';
import {getResourceNameWithAscendants} from '../../../../utils/resource-utils';
import ErrorAlertComponent from '../../../../components/ErrorAlert.component';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ShareIcon from '@mui/icons-material/Share';
import SafetyModel from '../../../../@types/safety-model';
import SafetyService from '../../../../services/safety.service';
import {SafetyModelEditorProps} from '../../../../@types/editor-props';
import {ResourceAction} from '../../../../@types/resource-action';
import {useUrlActions} from '../../../../hooks/useUrlActions';

const SafetyModelEditor: React.FC<SafetyModelEditorProps> = (props: SafetyModelEditorProps) => {
    const { safetyModel: theSafetyModel, isActive, updateTab } = props;
    const [name, setName] = useState(theSafetyModel?.name || '');
    const [description, setDescription] = useState(theSafetyModel?.description || '');
    const [exposure, setExposure] = useState(theSafetyModel?.exposure || 0);
    const [error, setError] = useState<string|null>(null);
    const [safetyModelGroupId, setSafetyModelGroupId] = React.useState<string|null>(theSafetyModel?.group?.id ? String(theSafetyModel.group.id) : null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const uuidRef = React.useRef(uuid());
    const { workspaceObjects, setWorkspaceObjects, setCurrentWorkspaceObject, currentAction, setCurrentAction, getAdditionalResource, publishResourceUpdate } = useWorkspaceContext();
    const { generateShareLink } = useUrlActions();

    const handleParentGroupChange = (event: any): void => {
        setSafetyModelGroupId(event.target.value as string);
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setName(event.target.value);
    };

    const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setDescription(event.target.value);
    };

    const handleExposureChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setExposure(parseInt(event.target.value));
    };

    const formSafetyModel = (): SafetyModel => {
        const safetyModelData = {} as SafetyModel;
        safetyModelData.name = name ?? '';
        safetyModelData.description = description;
        
        if (exposure > 0)
            safetyModelData.exposure = exposure;

        
        if (theSafetyModel && theSafetyModel.id) {
            safetyModelData.id = theSafetyModel.id;
        }
        safetyModelData.group = new Group();
        if (safetyModelGroupId != null) {
            safetyModelData.group.id = parseInt(safetyModelGroupId);
        } else {
            safetyModelData.group.id = groups[0].id;
        }
        return safetyModelData;
    }

    
    const updateSafetyModel = (safetyModel: SafetyModel): void => {
        let safetyModelObject = {objectType: ResourceType.SAFETY_MODELS, object: safetyModel};
        setCurrentWorkspaceObject(safetyModelObject);
        if (workspaceObjects?.objectType === ResourceType.SAFETY_MODELS) {
            setWorkspaceObjects({ objectType: ResourceType.SAFETY_MODELS });
        }
        
        // Publish update event for other components
        publishResourceUpdate({
            type: ResourceType.SAFETY_MODELS,
            id: safetyModel.id,
            updatedData: safetyModel,
            source: 'save'
        });
        
        updateTab(safetyModelObject);
    }

    const handleSave = (): void => {
        saveSafetyModel();
    }

    const saveSafetyModel = (): void => {
        if (name) {
            setError(null);
            if (theSafetyModel.id) {
                SafetyService.patchSafetyModel(formSafetyModel())
                    .then((safetyModel: SafetyModel) => {
                        updateSafetyModel(safetyModel);
                    })
                    .catch((err: Error) => {
                        setError(err.message);
                    });
            } else {
                SafetyService.createSafetyModel(formSafetyModel())
                    .then((safetyModel: SafetyModel) => {
                        updateSafetyModel(safetyModel);
                    })
                    .catch((err: Error) => {
                        setError(err.message);
                    });
            }
        } else {
            setError('Safety Model name is required');
        }
        
    };

    const isNotEditable = (): boolean => theSafetyModel.id !== null && theSafetyModel.id !== undefined && !theSafetyModel.group.canAddOrEditResources();

    const handleShare = (): void => {
        if (theSafetyModel.id) {
            const link = generateShareLink(ResourceType.SAFETY_MODELS, theSafetyModel.id.toString());
            
            // Copy to clipboard
            navigator.clipboard.writeText(link).then(() => {
                setSnackbarOpen(true);
            }).catch(() => {
                // Fallback for browsers that don't support clipboard API
                setError('Unable to copy link to clipboard');
            });
        }
    };

    // Track the current safety model ID to detect when editing a different model
    const currentSafetyModelIdRef = React.useRef(theSafetyModel?.id);
    
    useEffect(() => {
        if (theSafetyModel) {
            // Only reset form values if we're editing a different safety model
            if (currentSafetyModelIdRef.current !== theSafetyModel.id) {
                currentSafetyModelIdRef.current = theSafetyModel.id;
                if (theSafetyModel.id) {
                    setName(theSafetyModel.name);
                    setDescription(theSafetyModel.description || '');
                    setExposure(theSafetyModel.exposure ?? 0);
                }
                if (theSafetyModel.group && theSafetyModel.group.id) {
                    setSafetyModelGroupId(theSafetyModel.group.id.toString());
                }
            }
            
            if (isActive && currentAction === ResourceAction.SAVE) {
                setCurrentAction(null);
                saveSafetyModel();
            }
        }

        // eslint-disable-next-line
    }, [theSafetyModel, currentAction, isActive]);

    let groups = getAdditionalResource(ResourceType.GROUPS).resources as Group[] || [];
    groups.sort((a, b) => a.name.localeCompare(b.name))

    return (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            {/* Share button in top-left corner */}
            {theSafetyModel.id && (
                <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1000 }}>
                    <Tooltip title="Copy share link">
                        <IconButton onClick={handleShare} size="small" sx={{ backgroundColor: 'white', boxShadow: 1 }}>
                            <ShareIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
            
            <FormGroup className='formStyle'>
                <Stack className='formControl'>
                    <TextField
                        label="Safety Model Name"
                        aria-label='safety model name'
                        type='text'
                        variant="outlined"
                        size='small'
                        id={`name-input-${uuidRef.current}`}
                        autoComplete='on'
                        value={name || ''}
                        onChange={handleNameChange}
                        sx={{
                            '& input': {
                                border: 'none',
                            },
                            '& input.Mui-disabled': {
                                cursor: 'not-allowed',
                            }

                        }}
                        slotProps={{
                            input: {
                                'role': 'textbox',
                                'tabIndex': 0,
                                'aria-labelledby': `name-input-${uuidRef.current}`,
                                'aria-label': 'safety model name'
                            }
                        }}
                        disabled={isNotEditable()}
                    />
                    <hr />
                    <TextField
                        label='Safety Model Description (Optional)'
                        aria-label='safety model description'
                        type='text'
                        variant='outlined'
                        size='small'
                        id={`description-input-${uuidRef.current}`}
                        autoComplete='on'
                        value={description || ''}
                        onChange={handleDescriptionChange}
                        minRows={3}   
                        multiline
                        sx={{
                            '& input': {
                                border: 'none',
                            },
                            '& textarea.Mui-disabled': {
                                cursor: 'not-allowed',
                            }
                        }}
                        slotProps={{
                            input: {
                                'role': 'textbox',
                                'tabIndex': 0,
                                'aria-labelledby': `description-input-${uuidRef.current}`,
                                'aria-label': 'safety model description'
                            }
                        }}
                        disabled={isNotEditable()}
                    />
                    <hr />
                    <TextField
                        label='Exposure (Optional)'
                        aria-label='safety model exposure'
                        type='number'
                        variant='outlined'
                        size='small'
                        id={`exposure-input-${uuidRef.current}`}
                        autoComplete='on'
                        value={exposure || ''}
                        onChange={handleExposureChange}
                        sx={{
                            '& input': {
                                border: 'none',
                            },
                            '& input.Mui-disabled': {
                                cursor: 'not-allowed',
                            }
                        }}
                        slotProps={{
                            input: {
                                'role': 'textbox',
                                'tabIndex': 0,
                                'aria-labelledby': `exposure-input-${uuidRef.current}`,
                                'aria-label': 'safety model exposure'
                            }
                        }}
                        disabled={isNotEditable()}
                    />
                    <hr />
                    {groups.length > 0 ?
                        <FormControl
                            disabled={isNotEditable()}
                            sx={{
                                '& .MuiInputBase-input.Mui-disabled': {
                                    cursor: 'not-allowed',
                                },
                                '& .MuiSelect-select.Mui-disabled': {
                                    cursor: 'not-allowed',
                                },
                            }}
                        >
                            <InputLabel tabIndex={-1} id={`select-label-${uuidRef.current}`} htmlFor={`select-list-${uuidRef.current}`}>Working Area</InputLabel>
                            <Select
                                role='presentation'
                                labelId={`select-label-${uuidRef.current}`}
                                id={`group-label-${uuidRef.current}`}
                                size='small'
                                label='Working Area'
                                value={safetyModelGroupId || groups[0]?.id}
                                onChange={handleParentGroupChange}
                                inputProps={{
                                    'role': 'textbox',
                                    'tabIndex': 0,
                                    'id': `select-list-${uuidRef.current}`,
                                }}
                            >
                                {isNotEditable() ?
                                    <MenuItem key={theSafetyModel.group.id} value={theSafetyModel.group.id} dense>{getResourceNameWithAscendants(theSafetyModel.group, ResourceType.GROUPS)}</MenuItem>
                                    :
                                    groups.filter(group => group.canAddOrEditResources()).map((group) => (
                                        <MenuItem key={group.id} value={group.id} dense>{getResourceNameWithAscendants(group, ResourceType.GROUPS)}</MenuItem>
                                    ))}
                            </Select>

                        </FormControl> : null
                    }
                    <hr />
               
                    <div>
                        <Button className='formBtn' id={`save-safety-model-${uuidRef.current}`}
                            size='small' variant='contained'
                            aria-label='save safety model'
                            title='Save Safety Model'
                            onClick={handleSave}
                            startIcon={<SaveAltIcon />}
                            disabled={isNotEditable()}
                            sx={{
                                '&:disabled': {     
                                    cursor: 'not-allowed',
                                    pointerEvents: 'all !important',
                                }
                            }}
                        >
                        Save
                        </Button>
                    </div>
            
                    {error ? <ErrorAlertComponent message={error} />  : null}
                
                </Stack>
            </FormGroup>
            
            {/* Snackbar for share link notification */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={(): void => setSnackbarOpen(false)}
                message="Share link copied to clipboard!"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </Box>
    );
};

export default SafetyModelEditor;
